/**
 * Hook for fetching course data from the Learn Anything API.
 *
 * Replaces hardcoded MOCK_COURSES / MOCK_*_CURRICULUM with real API calls.
 */

import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface CourseListItem {
  courseId: string;
  slug: string;
  title: string;
  description: string | null;
  difficultyLevel: string;
  estimatedDurationHours: number | null;
  tags: string[];
  status: string;
}

interface CurriculumLesson {
  lessonId: string;
  title: string;
  lessonType: string;
  sequenceOrder: number;
  estimatedDurationMinutes: number | null;
}

interface CurriculumModule {
  moduleId: string;
  title: string;
  description: string | null;
  sequenceOrder: number;
  estimatedDurationMinutes: number | null;
  lessons: CurriculumLesson[];
}

interface Curriculum extends CourseListItem {
  modules: CurriculumModule[];
}

interface LessonProgressEntry {
  correctCount: number;
  targetQuizCount: number;
  status: string;
}

export function useCourseData() {
  const queryClient = useQueryClient();

  // Fetch published courses on mount
  const { data: coursesData, isLoading: coursesLoading } = useQuery({
    queryKey: queryKeys.learnAnything.courses(),
    queryFn: () => api.listCourses(),
  });

  const courses: CourseListItem[] = coursesData?.courses ?? [];

  const fetchCurriculum = useCallback(
    async (slug: string): Promise<Curriculum | null> => {
      try {
        return await queryClient.fetchQuery({
          queryKey: queryKeys.learnAnything.curriculum(slug),
          queryFn: () => api.getCourseCurriculum(slug),
        });
      } catch {
        return null;
      }
    },
    [queryClient],
  );

  const fetchProgress = useCallback(
    async (slug: string): Promise<Record<string, LessonProgressEntry>> => {
      try {
        const res = await queryClient.fetchQuery({
          queryKey: queryKeys.learnAnything.progress(slug),
          queryFn: () => api.getCourseProgress(slug),
        });
        return res.lessons ?? {};
      } catch {
        return {};
      }
    },
    [queryClient],
  );

  const enrollMutation = useMutation({
    mutationFn: (slug: string) => api.enrollCourse(slug),
    onSuccess: (_data, slug) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.learnAnything.progress(slug) });
    },
  });

  const enrollCourse = useCallback(
    async (slug: string): Promise<void> => {
      try {
        await enrollMutation.mutateAsync(slug);
      } catch {
        // Already enrolled or network error — safe to ignore
      }
    },
    [enrollMutation],
  );

  return {
    courses,
    coursesLoading,
    fetchCurriculum,
    fetchProgress,
    enrollCourse,
  };
}
